import {getBackendErrorMessage} from "../../shared/utils/httpError";
export {extractListPayload} from "../../shared/utils/payload";

export const PERMISSION_ACTION_OPTIONS = ['VISUALIZAR', 'CRIAR', 'EDITAR', 'EXCLUIR', 'APROVAR', 'EXPORTAR'];

export const formatDateBR = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
};

export const getBackendMessage = (error, fallbackMessage) => getBackendErrorMessage(error, fallbackMessage);

export const normalizePermissionRows = (items = []) => items.map((item) => {
    const license = item.licenca || item.license || null;
    const customer = license?.customer || license?.empresa || null;
    const plan = license?.plan || license?.plano || null;
    const productSystem = plan?.productSystem || plan?.sistema || null;

    return {
        ...item,
        id: item.id || item.roleId || '',
        roleId: item.roleId || item.id || '',
        id_licenca: item.id_licenca || item.licenseId || item.licenca?.id || item.license?.id || '',
        licenseId: item.licenseId || item.id_licenca || item.licenca?.id || item.license?.id || '',
        licenca: license ? {
            ...license,
            id: license.id || '',
            tenant_id: license.tenant_id || license.tenantId || '',
            tenantId: license.tenantId || license.tenant_id || '',
            validade_inicio: license.validade_inicio || license.validFrom || '',
            validade_fim: license.validade_fim || license.validUntil || '',
            validade_inicio_fmt: formatDateBR(license.validade_inicio || license.validFrom),
            validade_fim_fmt: formatDateBR(license.validade_fim || license.validUntil),
            empresa: customer ? {
                ...customer,
                id: customer.id || '',
                razao_social: customer.razao_social || customer.legalName || '',
                nome: customer.nome || customer.legalName || '',
            } : null,
            customer,
            plano: plan ? {
                ...plan,
                id: plan.id || '',
                nome_plano: plan.nome_plano || plan.productPlanName || '',
                status: plan.status || '',
                sistema: productSystem ? {
                    ...productSystem,
                    id: productSystem.id || '',
                    nome: productSystem.nome || productSystem.systemName || '',
                } : null,
            } : null,
            plan,
            status: license.status || '',
            active: license.active,
            currentlyValid: license.currentlyValid,
        } : item.licenca,
        acoes: Array.isArray(item.acoes) ? item.acoes : [],
        permissions: Array.isArray(item.permissions)
            ? item.permissions.map((permission) => ({
                ...permission,
                id: permission.id || permission.permissionId || permission.resourceId || permission.resource?.id || '',
                resourceId: permission.resourceId || permission.resource?.id || '',
                resource: permission.resource || {
                    id: permission.resourceId || '',
                    resourceName: permission.resourceName || permission.nome || '-',
                    endpoint: permission.endpoint || '-',
                    httpMethod: permission.httpMethod || permission.metodo || 'GET',
                },
            }))
            : Array.isArray(item.acoes)
                ? item.acoes.map((acao) => ({nome: acao, allowed: true}))
                : [],
        roleName: item.roleName || item.nome || '',
        description: item.description || item.descricao || '',
        usuarios_vinculados: Number(item.usuarios_vinculados || 0),
        criado_em: item.criado_em || item.createdAt || '',
        atualizado_em: item.atualizado_em || item.updatedAt || '',
        ultima_revisao: item.ultima_revisao || item.updatedAt || '',
        criado_em_fmt: formatDateBR(item.criado_em || item.createdAt),
        atualizado_em_fmt: formatDateBR(item.atualizado_em || item.updatedAt),
        ultima_revisao_fmt: formatDateBR(item.ultima_revisao || item.updatedAt),
    };
});

export const countPermissions = (permissions = []) => ({
    liberadas: permissions.filter((item) => item?.allowed).length,
    bloqueadas: permissions.filter((item) => !item?.allowed).length,
});

const getModuloId = (item) => item?.id_modulo || item?.modulo?.id || item?.moduleId || item?.module?.id || item?.id || '';

export const getPlanModuloIds = (plan) => {
    if (!plan) return null;

    const planModules = Array.isArray(plan.modulos)
        ? plan.modulos
        : Array.isArray(plan.modules)
            ? plan.modules
            : null;

    if (!planModules) return null;

    return new Set(planModules.map(getModuloId).filter(Boolean));
};

export const buildPermissionTree = ({sistemas = [], modulos = [], funcionalidades = [], recursos = []} = {}, options = {}) => {
    const allowedModuloIds = options.allowedModuloIds || null;
    const modulosFiltrados = allowedModuloIds
        ? modulos.filter((modulo) => allowedModuloIds.has(modulo.id))
        : modulos;

    // Desde a migration 0064 a funcionalidade tem um módulo só, em `modulo_id`; as listas ficam de reserva.
    const funcionalidadesPorModulo = funcionalidades.reduce((acc, item) => {
        const moduloIds = item.modulo_id
            ? [item.modulo_id]
            : item.modulo_ids?.length
                ? item.modulo_ids
                : item.modulos?.map((modulo) => modulo.id).filter(Boolean) || [];
        moduloIds.forEach((moduloId) => {
            if (!acc[moduloId]) acc[moduloId] = [];
            acc[moduloId].push(item);
        });
        return acc;
    }, {});

    const recursosPorFuncionalidade = recursos.reduce((acc, item) => {
        const funcionalidadeId = item.funcionalidade_id || item.funcionalidade?.id;
        if (!funcionalidadeId) return acc;
        if (!acc[funcionalidadeId]) acc[funcionalidadeId] = [];
        acc[funcionalidadeId].push(item);
        return acc;
    }, {});

    return modulosFiltrados.map((modulo) => ({
        ...modulo,
        nome: modulo.nome || '-',
        sistema: sistemas.find((item) => item.id === (modulo.sistema_id || modulo.id_sistema || modulo.sistemas?.[0]?.id)) || null,
        funcionalidades: (funcionalidadesPorModulo[modulo.id] || []).map((funcionalidade) => ({
            ...funcionalidade,
            recursos: (recursosPorFuncionalidade[funcionalidade.id] || []).map((recurso) => ({
                ...recurso,
                resourceName: recurso.resourceName || recurso.nome || '-',
                endpoint: recurso.endpoint || '-',
                httpMethod: recurso.httpMethod || recurso.metodo || 'GET',
            })),
        })),
    })).filter((modulo) => modulo.funcionalidades.length > 0);
};
